import { PF_HW } from '../../sim/constants';
import { Rank } from '../../sim/defs';
import { cos } from '../../sim/math';
import { defineEnemy, Motion } from '../schema';

/** E-I2 — GUÊPE. Le professeur du SANG-FROID.
 *
 *  Elle se perche au-dessus de la mêlée puis HARCÈLE : elle fond en piqué vers le
 *  joueur, se dérobe et remonte AVANT de le toucher, puis replonge. Encore, et
 *  encore. Sa pression est purement corporelle — pas un tir, un corps qui feinte.
 *
 *  ── Ce qu'elle enseigne ──
 *  À ne pas mordre à la feinte. Le Faucon commet UN piqué franc, à pleine
 *  vitesse, qu'on casse au moment du départ ; la Guêpe, elle, ne s'engage jamais
 *  vraiment. Chaque plongée est bornée et se rétracte. Le réflexe est de fuir dès
 *  qu'elle amorce — mais elle amorce sans cesse, et fuir à chaque amorce épuise
 *  et finit par vous jeter sous un vrai passage. La bonne réponse est d'attendre
 *  qu'elle soit réellement sur vous, de faire UN pas net, et de laisser la
 *  remontée rouvrir l'espace. C'est l'esquive tardive du missile guidé, mais
 *  contre une masse qui revient à la charge : la leçon n'est plus le timing d'un
 *  seul geste, c'est le calme de le répéter.
 *
 *  ── Le hook plutôt qu'une forme du moteur (PLAN.md §3.3) ──
 *  « Piquer vers le joueur puis remonter, en boucle » n'est aucune des formes du
 *  moteur (le Dard et le Faucon plongent UNE fois et ne remontent pas). On
 *  l'exprime dans `onUpdate` — pur, sans allocation, la seule transcendante étant
 *  `cos` de la table déterministe. La DESCENTE d'approche jusqu'au perchoir reste
 *  au moteur (Hover) : accélération d'entrée et protection d'apparition gratuites.
 *
 *  ── Ce qui garde l'esquive vivante (MESURÉ) ──
 *  Deux garde-fous, vérifiés dans un vrai World (tests/content/intercepteurs) :
 *    · la plongée VISE le joueur mais à vitesse latérale BORNÉE (`SEEK` = 300,
 *      sous les 480 du joueur) : un pas franc pendant la plongée la sème ;
 *    · surtout, la REMONTÉE est une vraie dérobade — la Guêpe repart tout en haut
 *      (`HOLD_Y`), hors de portée du joueur, à CHAQUE cycle. Ce n'est pas une
 *      politesse, c'est la fenêtre : elle rouvre franchement l'espace entre deux
 *      plongées. Un joueur qui se décale au bon moment n'est jamais cueilli. */

/** Perchoir de la Guêpe, en unités. Au-dessus de la rangée du joueur (−380) :
 *  entre deux plongées, elle est franchement hors de portée. */
const HOLD_Y = -40;
/** Profondeur d'une plongée, en unités. HOLD_Y − REACH = −400 : la plongée
 *  TRAVERSE la rangée du joueur (−380), donc menace pour de vrai, puis remonte. */
const REACH = 360;
/** Cadence des plongées, en rad/s (période ≈ 3,1 s). */
const OMEGA = 2.0;
/** Vitesse latérale de traque pendant la plongée, en u/s. Sous les 480 du
 *  joueur : la plongée est visée mais jamais un verrou parfait. */
const SEEK = 300;
/** Plafond de la vitesse verticale de suivi de la plongée, en u/s. Au-dessus de
 *  la vitesse de pointe du piqué (~470) pour que la Guêpe atteigne bien le fond
 *  de sa plongée sans saut à la prise de main du hook. */
const VDIVE = 560;
/** Garde au bord : elle ne colle jamais le mur, où elle coincerait le joueur
 *  contre la paroi sans échappée (même règle que la Fauche). */
const LIMIT = PF_HW - 40;

export default defineEnemy({
  id: 'guepe',
  rank: Rank.Standard,
  hp: 80,
  visualRadius: 20,
  score: 170,
  telegraph: 0.8,
  drops: 1,
  // Hover jusqu'au perchoir HOLD_Y, amplitude ZÉRO : le moteur ne fait que la
  // descente d'approche. Tout le harcèlement (piqué + remontée + traque) est au
  // hook.
  motion: { kind: Motion.Hover, speed: 300, amplitude: 0, frequency: 0, holdY: HOLD_Y },
  onUpdate: (slot, age, dt, ctx) => {
    let x = ctx.x[slot]!;
    let y = ctx.y[slot]!;

    // Montée en régime de la traque à l'apparition (le moteur, lui, entre à 40 %).
    const ramp = age < 0.5 ? age * 2 : 1;
    // Traque latérale BORNÉE, commune à la descente et aux plongées.
    const tx = ctx.playerX;
    const stepX = SEEK * dt * ramp;
    if (tx - x > stepX) x += stepX;
    else if (tx - x < -stepX) x -= stepX;
    else x = tx;
    if (x > LIMIT) x = LIMIT;
    else if (x < -LIMIT) x = -LIMIT;
    ctx.x[slot] = x;

    // Tant qu'elle n'a pas atteint son perchoir, on laisse le moteur la
    // descendre (Y intact) : on ne touche qu'à l'abscisse.
    if (y > HOLD_Y + 6) return;

    // Profondeur de plongée : 0 (perchée) → 1 (fond du piqué) → 0. Le carré
    // écrase la courbe vers le bas : la Guêpe reste PERCHÉE l'essentiel du cycle
    // — la longue fenêtre franche — et ne pique que par brèves saccades. Un piqué
    // qui s'attarderait au fond redeviendrait une poursuite collante.
    const b = (1 - cos(age * OMEGA)) * 0.5;
    const depth = b * b;
    const targetY = HOLD_Y - REACH * depth;
    // Suivi BORNÉ en Y : pas de saut au moment où le hook prend la main sur Y.
    const stepY = VDIVE * dt;
    if (targetY - y > stepY) y += stepY;
    else if (targetY - y < -stepY) y -= stepY;
    else y = targetY;
    ctx.y[slot] = y;
  },
});
